import { useEffect, type ClipboardEvent } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';
import Box from '@mui/material/Box';
import type { FieldDef, FieldOption, FormValues } from '../types';
import BulkImportActions, { buildBulkImportValue, parseBulkImportText } from './BulkImportActions';
import {
  normalizeProductCode,
  ProductValidationPreview,
  useProductValidation,
} from './ProductValidationPreview';
import {
  findMatchingSellerOption,
  getSellerOptionDisplayValue,
  isSellerFieldKey,
  isSellerOptionExcluded,
  normalizeSellerValues,
} from '../../shared/utils/sellerValues';

interface TagsFieldProps {
  field: FieldDef;
  values: FormValues;
  onChange: (key: string, value: string[]) => void;
  externalError?: string;
}

const filter = createFilterOptions<FieldOption>();
const TAG_SPLIT_RE = /[\n\r\t,;|]+/;

function splitTagText(raw: string): string[] {
  return raw
    .split(TAG_SPLIT_RE)
    .map((v) => v.trim())
    .filter(Boolean);
}

function uniqueTags(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function toTags(raw: string | string[] | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return uniqueTags(raw.flatMap((value) => splitTagText(String(value))));
  return uniqueTags(splitTagText(raw));
}

function getConflicts(tags: string[], otherRaw: string | string[] | undefined): Set<string> {
  const other = new Set(toTags(otherRaw));
  return new Set(tags.filter((t) => other.has(t)));
}

function getClipboardText(event: ClipboardEvent<HTMLElement>) {
  return event.clipboardData.getData('text').trim();
}

export default function TagsField({ field, values, onChange, externalError }: TagsFieldProps) {
  const rawValue = values[field.key] as string | string[] | undefined;
  const tags = toTags(rawValue);
  const tagsKey = tags.join('\u001f');
  const otherRaw = field.excludeValuesFrom ? (values[field.excludeValuesFrom] as string | string[] | undefined) : undefined;
  const isSellerField = isSellerFieldKey(field.key);
  const options: FieldOption[] = field.options ?? [];
  const optionKey = isSellerField ? options.map((option) => `${option.value}:${option.label}`).join('\u001f') : '';
  const normalizedTags = isSellerField ? normalizeSellerValues(tags, options) : tags;
  const normalizedOtherTags = isSellerField ? normalizeSellerValues(toTags(otherRaw), options) : toTags(otherRaw);
  const excludedValues = new Set(normalizedOtherTags);
  const conflicts = otherRaw !== undefined
    ? isSellerField
      ? new Set(normalizedTags.filter((tag) => excludedValues.has(tag)))
      : getConflicts(tags, otherRaw)
    : new Set<string>();
  const hasConflict = conflicts.size > 0;
  const conflictText = hasConflict
    ? `Valor(es) em conflito com o campo oposto: ${Array.from(conflicts).join(', ')}`
    : undefined;
  const productValidation = useProductValidation(normalizedTags, field.key, values);
  const hasMissingProducts = productValidation.enabled && productValidation.missing.length > 0;
  const productValidationText = hasMissingProducts
    ? `${productValidation.missing.length} SKU/EAN nao encontrado(s). Veja o detalhe abaixo.`
    : undefined;

  const availableOptions = options.filter((option) => (
    isSellerField
      ? !isSellerOptionExcluded(option, normalizedOtherTags)
      : !excludedValues.has(option.value)
  ));

  const optionExcludedValues = isSellerField
    ? normalizedOtherTags.flatMap((value) => {
        const option = findMatchingSellerOption(value, options);
        return option ? [value, option.value, getSellerOptionDisplayValue(option)] : [value];
      })
    : Array.from(excludedValues);

  const selectedOptions: FieldOption[] = normalizedTags.map((t) => {
    const found = isSellerField
      ? findMatchingSellerOption(t, options)
      : options.find((o) => o.value === t);
    return found ?? { value: t, label: t };
  });

  useEffect(() => {
    const rawTags = Array.isArray(rawValue)
      ? rawValue.map((value) => String(value).trim()).filter(Boolean)
      : rawValue
        ? [String(rawValue).trim()]
        : [];

    const nextTags = isSellerField ? normalizedTags : tags;

    if (rawTags.length === nextTags.length && rawTags.every((value, index) => value === nextTags[index])) return;

    onChange(field.key, nextTags);
  }, [field.key, isSellerField, rawValue, tagsKey, optionKey]);

  const normalizeAppliedTags = (nextTags: string[]): string[] => {
    if (!isSellerField) return uniqueTags(nextTags).filter((value) => !excludedValues.has(value));
    return normalizeSellerValues(nextTags, options, normalizedOtherTags);
  };

  const handlePaste = (event: ClipboardEvent<HTMLElement>) => {
    const text = getClipboardText(event);
    if (!text) return;

    const rawValues = parseBulkImportText(text);
    const hasMultipleValues = rawValues.length > 1 || TAG_SPLIT_RE.test(text);
    if (!hasMultipleValues) return;

    event.preventDefault();

    if (!field.bulkImport) {
      onChange(field.key, normalizeAppliedTags([...normalizedTags, ...rawValues]));
      return;
    }

    const result = buildBulkImportValue({
      mode: options.length > 0 ? 'options' : 'text',
      options: availableOptions,
      currentValue: normalizedTags,
      excludedValues: optionExcludedValues,
      rawValues,
    });

    if (!result.applied || result.value === undefined) return;

    onChange(field.key, normalizeAppliedTags(Array.isArray(result.value) ? result.value : toTags(result.value)));
  };

  const getChipColor = (value: string): ChipProps['color'] => {
    const normalizedValue = normalizeProductCode(value);

    if (conflicts.has(value) || productValidation.missingSet.has(normalizedValue)) return 'error';
    if (productValidation.productByCode.has(normalizedValue)) return 'success';

    return 'default';
  };

  const getChipTitle = (value: string): string | undefined => {
    const normalizedValue = normalizeProductCode(value);
    const product = productValidation.productByCode.get(normalizedValue);

    if (product) return product.nomeProduto;
    if (productValidation.missingSet.has(normalizedValue)) return 'SKU/EAN nao encontrado';

    return undefined;
  };

  return (
    <Box>
      <Autocomplete
      multiple
      freeSolo
      size="small"
      options={availableOptions}
      value={selectedOptions}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) => (
        isSellerField
          ? Boolean(findMatchingSellerOption(val.value || val.label, [opt]))
          : opt.value === val.value
      )}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const inputValue = params.inputValue.trim();
        const alreadyExists = isSellerField
          ? Boolean(findMatchingSellerOption(inputValue, opts))
          : opts.some((o) => o.value === inputValue || o.label.toLowerCase() === inputValue.toLowerCase());

        if (inputValue && !alreadyExists) {
          filtered.push({ value: inputValue, label: `Adicionar "${inputValue}"` });
        }
        return filtered;
      }}
      onChange={(_e, newValue) => {
        const newTags = newValue.flatMap((v) => {
          if (typeof v === 'string') return v;
          if (v.label.startsWith('Adicionar "')) return v.value;
          if (isSellerField) return getSellerOptionDisplayValue(v);
          return v.value;
        });
        onChange(field.key, normalizeAppliedTags(newTags.flatMap((value) => splitTagText(value))));
      }}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const chipValue = typeof opt === 'string'
            ? opt
            : isSellerField
              ? getSellerOptionDisplayValue(opt)
              : opt.value;
          const chipLabel = typeof opt === 'string'
            ? opt
            : opt.label.startsWith('Adicionar "')
              ? opt.value
              : opt.label;

          return (
            <Chip
              key={key}
              label={chipLabel}
              size="small"
              color={getChipColor(chipValue)}
              title={getChipTitle(chipValue)}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          required={field.required}
          placeholder={normalizedTags.length === 0 ? (field.placeholder ?? 'Selecione ou digite') : undefined}
          error={!!externalError || hasConflict}
          helperText={externalError ?? conflictText ?? productValidationText ?? field.helperText}
          onPaste={handlePaste}
        />
      )}
      />
      <ProductValidationPreview validation={productValidation} />
      {field.bulkImport && (
        <BulkImportActions
          mode={options.length > 0 ? 'options' : 'text'}
          options={availableOptions}
          currentValue={normalizedTags}
          excludedValues={optionExcludedValues}
          onApply={(nextValue) => onChange(field.key, normalizeAppliedTags(Array.isArray(nextValue) ? nextValue : toTags(nextValue)))}
        />
      )}
    </Box>
  );
}
